import { toast } from "sonner";
import { baseApi } from "../api/baseApi";
import {
  SecureVolumeWorkContainer,
  SecureVolumeCustomerListItem,
  SaveSecureVolumeRequest,
  SecureVolumeProduct,
  PopulateSecureVolumeResponse,
} from "../types/secureVolume.types";
import { SecureVolumeAdminOverview } from "../types/secureVolumeAdmin.types";

const buildQuery = (params: Record<string, number | undefined>) => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value != null) searchParams.set(key, value.toString());
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
};

/**
 * Backend levererar produktlistan i camelCase (se API-dokumentationen), men övriga
 * securevolume-svar är PascalCase. Normalisera så att UI:t alltid får camelCase.
 */
const normalizeProduct = (raw: Record<string, unknown>): SecureVolumeProduct => {
  const pick = <T,>(camel: string, pascal: string) =>
    (raw[camel] ?? raw[pascal]) as T;
  return {
    productNo: pick<number>("productNo", "ProductNo"),
    productName: pick<string>("productName", "ProductName"),
    managementLengthMonths: pick<number>("managementLengthMonths", "ManagementLengthMonths"),
    managementStartMonth: pick<number>("managementStartMonth", "ManagementStartMonth"),
    managementStartDay: pick<number>("managementStartDay", "ManagementStartDay"),
    managementPeriodStart: pick<string>("managementPeriodStart", "ManagementPeriodStart"),
    managementPeriodEnd: pick<string>("managementPeriodEnd", "ManagementPeriodEnd"),
    defaultTargetYear: pick<number>("defaultTargetYear", "DefaultTargetYear"),
  };
};

export const secureVolumeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /** Bolagets volymsäkringsprodukter med beräknad förvaltningsperiod och målår */
    getSecureVolumeProducts: builder.query<SecureVolumeProduct[], void>({
      query: () => ({
        url: `/v2/crm/securevolume/products`,
      }),
      transformResponse: (response: Record<string, unknown>[]) =>
        (response ?? []).map(normalizeProduct),
    }),
    getSecureVolumeAdminOverview: builder.query<
      SecureVolumeAdminOverview,
      { year?: number; productNo?: number } | void
    >({
      query: (args) => ({
        url: `/v2/crm/securevolume/admin/overview${buildQuery({
          year: args?.year,
          productNo: args?.productNo,
        })}`,
      }),
      providesTags: ["Work"],
    }),
    /**
     * Utan productNo används bolagets ursprungsprodukt.
     * targetYear är ett rent visningsfilter för att se tidigare års listor.
     */
    getSecureVolumeCustomers: builder.query<
      SecureVolumeCustomerListItem[],
      { productNo?: number; targetYear?: number } | void
    >({
      query: (args) => ({
        url: `/v2/crm/securevolume/customers${buildQuery({
          productNo: args?.productNo,
          targetYear: args?.targetYear,
        })}`,
      }),
      providesTags: ["Work"],
    }),
    getSecureVolumeWork: builder.query<
      SecureVolumeWorkContainer,
      { customerId: number; workId: number }
    >({
      query: ({ customerId, workId }) => ({
        url: `/v2/crm/securevolume/${customerId}/work/${workId}`,
      }),
      providesTags: (_result, _error, { workId }) => [
        { type: "Work", id: workId },
      ],
    }),
    /**
     * Målåret härleds alltid av backend från produkten – portalen skickar
     * medvetet inte targetYear, så att ärenden inte kan skapas för andra år.
     */
    populateSecureVolumeWork: builder.mutation<
      PopulateSecureVolumeResponse,
      { customerNo: number; productNo?: number }
    >({
      query: ({ customerNo, productNo }) => ({
        url: `/v2/crm/securevolume/populate${buildQuery({
          productNo,
          customerNo,
        })}`,
        method: "POST",
      }),
      invalidatesTags: ["Work"],
    }),
    saveSecureVolumeWork: builder.mutation<void, SaveSecureVolumeRequest>({
      query: ({ customerNo, workId, body }) => ({
        url: `/v2/crm/securevolume/${customerNo}/work/${workId}/volumes`,
        method: "PUT",
        body,
      }),
      async onQueryStarted(
        { customerNo, workId, body },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          secureVolumeApi.util.updateQueryData(
            "getSecureVolumeWork",
            { customerId: customerNo, workId },
            (draft) => {
              const facility = draft.SecureVolumes.find(
                (f) => f.CustomerBillingPointNo === body.BillingPointId,
              );
              if (facility) {
                for (const incoming of body.QuarterVolumes) {
                  const idx = facility.QuarterVolumes.findIndex(
                    (v) =>
                      v.Year === incoming.Year &&
                      v.Quarter === incoming.Quarter &&
                      v.VolumeType === incoming.VolumeType,
                  );
                  if (idx >= 0) {
                    facility.QuarterVolumes[idx] = incoming;
                  } else {
                    facility.QuarterVolumes.push(incoming);
                  }
                }
              }
            },
          ),
        );
        try {
          await queryFulfilled;
          toast.success("Sparat!");
          dispatch(secureVolumeApi.util.invalidateTags([{ type: "Work", id: workId }]));
        } catch {
          patchResult.undo();
          toast.error("Det gick inte att spara volymdata. Försök igen.");
        }
      },
    }),
    uploadSecureVolumeFile: builder.mutation<
      { eventDataNo: number; fileName: string },
      {
        customerId: number;
        workId: number;
        file: File;
        eventDataType?: number;
      }
    >({
      query: ({ customerId, workId, file, eventDataType }) => {
        const formData = new FormData();
        formData.append("document", file);
        if (eventDataType != null) formData.append("eventDataType", eventDataType.toString());
        return {
          url: `/v2/crm/securevolume/${customerId}/work/${workId}/upload`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { workId }) => [
        { type: "Work", id: workId },
      ],
    }),
    deleteEventData: builder.mutation<void, { eventDataId: number; workId?: number }>({
      query: ({ eventDataId }) => ({
        url: `/v2/crm/eventdata/${eventDataId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { workId }) =>
        workId ? [{ type: "Work", id: workId }] : ["Work"],
    }),
    /** Målåret kommer från ärendet – ingen year-parameter längre */
    downloadSecureVolumePdf: builder.mutation<null, { customerId: number; workId: number; type?: "full" | "appendix" | "appendix-nochange"; fromZynergy?: boolean }>({
      queryFn: async ({ customerId, workId, type, fromZynergy }, api, _extraOptions, baseQuery) => {
        const searchParams = new URLSearchParams();
        if (type) searchParams.set("type", type);
        if (fromZynergy) searchParams.set("fromZynergy", "true");
        const params = searchParams.toString() ? `?${searchParams}` : "";
        const result = await baseQuery({
          url: `/v2/crm/securevolume/${customerId}/work/${workId}/pdf${params}`,
          responseHandler: (response: Response) => response.blob(),
        });
        if (result.error) return { error: result.error };

        const blob = result.data as Blob;
        const url = URL.createObjectURL(blob);
        window.open(url);
        setTimeout(() => URL.revokeObjectURL(url), 60_000);

        api.dispatch(secureVolumeApi.util.invalidateTags([{ type: "Work", id: workId }]));

        return { data: null };
      },
    }),
    markSecureVolumeSent: builder.mutation<void, { customerId: number; workId: number }>({
      query: ({ customerId, workId }) => ({
        url: `/v2/crm/securevolume/${customerId}/work/${workId}/mark-sent`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { workId }) => [
        { type: "Work", id: workId },
      ],
    }),
    completeSecureVolume: builder.mutation<void, { customerId: number; workId: number }>({
      query: ({ customerId, workId }) => ({
        url: `/v2/crm/securevolume/${customerId}/work/${workId}/complete`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { workId }) => [
        { type: "Work", id: workId },
      ],
    }),
  }),
});

export const {
  useGetSecureVolumeProductsQuery,
  useGetSecureVolumeAdminOverviewQuery,
  useGetSecureVolumeCustomersQuery,
  useGetSecureVolumeWorkQuery,
  usePopulateSecureVolumeWorkMutation,
  useSaveSecureVolumeWorkMutation,
  useUploadSecureVolumeFileMutation,
  useDeleteEventDataMutation,
  useDownloadSecureVolumePdfMutation,
  useMarkSecureVolumeSentMutation,
  useCompleteSecureVolumeMutation,
} = secureVolumeApi;
